import React, { useState, type JSX } from 'react';

import { defineMessages, useIntl } from 'react-intl';

import { cssMap, cx } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Inline } from '@atlaskit/primitives/compiled/inline';
import { Pressable } from '@atlaskit/primitives/compiled/pressable';
import { Stack } from '@atlaskit/primitives/compiled/stack';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

const messages = defineMessages({
	showMore: {
		id: 'user-inline-preview.list.show-more',
		defaultMessage: 'Show more',
		description: 'Button label to expand and show more user cards in the list',
	},
	showLess: {
		id: 'user-inline-preview.list.show-less',
		defaultMessage: 'Show less',
		description: 'Button label to collapse and show fewer user cards in the list',
	},
});

const styles = cssMap({
	grid: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr)',
	},
	stack: {
		display: 'grid',
		gridTemplateColumns: 'minmax(0, 1fr)',
	},
	showMoreTrigger: {
		backgroundColor: 'transparent',
		paddingTop: '0',
		paddingBottom: '0',
		paddingLeft: '0',
		paddingRight: '0',
		width: 'auto',
	},
});

const gapStyles = cssMap({
	none: { gap: '0' },
	'space.050': { gap: token('space.050') },
	'space.075': { gap: token('space.075') },
	'space.100': { gap: token('space.100') },
	'space.150': { gap: token('space.150') },
	'space.200': { gap: token('space.200') },
});

type UserInlinePreviewListLayout = 'grid' | 'stack';
type UserInlinePreviewListGap =
	| 'none'
	| 'space.050'
	| 'space.075'
	| 'space.100'
	| 'space.150'
	| 'space.200';

interface UserInlinePreviewListProps {
	layout?: UserInlinePreviewListLayout;
	/**
	 * Gap between rendered child items. Defaults to `space.100`. Pass `none` when the
	 * consumer is rendering its own per-row separators
	 */
	gap?: UserInlinePreviewListGap;
	/**
	 * Caps how many child items are rendered. When set, extra children are omitted and no
	 * "Show more" control is shown (unlike `initialVisibleCount`).
	 */
	maxVisibleItems?: number;
	initialVisibleCount?: number;
	children: React.ReactNode;
	testId?: string;
}

/**
 * Renders a list of UserInlinePreview components in a grid or stack layout.
 * Use `initialVisibleCount` for expandable "Show more" behavior, or `maxVisibleItems` for a fixed cap
 * without expansion.
 */
export function UserInlinePreviewList({
	layout = 'grid',
	gap = 'space.100',
	maxVisibleItems,
	initialVisibleCount,
	children,
	testId = 'user-inline-preview-list',
}: UserInlinePreviewListProps): JSX.Element {
	const intl = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const childArray = React.Children.toArray(children);
	const isHardCapped = maxVisibleItems !== undefined;
	const cappedChildren = isHardCapped ? childArray.slice(0, maxVisibleItems) : childArray;
	const hasExpandOverflow =
		!isHardCapped && initialVisibleCount !== undefined && childArray.length > initialVisibleCount;
	const visibleChildren =
		hasExpandOverflow && !isExpanded ? childArray.slice(0, initialVisibleCount) : cappedChildren;

	const ChevronIcon = isExpanded ? ChevronDownIcon : ChevronRightIcon;

	return (
		<Stack space="space.075" testId={testId}>
			<Box xcss={cx(layout === 'grid' ? styles.grid : styles.stack, gapStyles[gap])}>
				{visibleChildren}
			</Box>

			{hasExpandOverflow && (
				<Pressable
					xcss={styles.showMoreTrigger}
					onClick={() => setIsExpanded((prev) => !prev)}
					aria-expanded={isExpanded}
					testId={`${testId}-show-more`}
				>
					<Inline space="space.025" alignBlock="center">
						<ChevronIcon label="" color={token('color.icon.subtle')} />
						<Text color="color.text.subtle">
							{isExpanded
								? intl.formatMessage(messages.showLess)
								: intl.formatMessage(messages.showMore)}
						</Text>
					</Inline>
				</Pressable>
			)}
		</Stack>
	);
}
